import { Settings as SettingsIcon, TrendingUp } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import SafePanel from '../../components/SafePanel';
import { DangerZone } from './DangerZone';
import { DbExportPanel } from './DbExportPanel';
import { AdminActivityLog } from './AdminActivityLog';
import { BroadcastMessagePanel } from './BroadcastMessagePanel';
import { FirstLoadMatchCampaignCard } from './FirstLoadMatchCampaignCard';
import { ApplePayDomainCard } from './ApplePayDomainCard';
import FreePlaySpendBanner from './FreePlaySpendBanner';

export const SettingsAdminTab = ({ settings, handleSettingsUpdate, onNavigate }) => {
    // Defensive default — if the parent hasn't hydrated settings yet, we
    // still render an empty shell instead of crashing on `settings.general?.…`.
    const s = settings || {};
    return (
    <>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="card-dark p-6">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <SettingsIcon className="w-5 h-5 text-[#FFD700]" />
                    General Settings
                </h3>
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <Label className="text-white">Maintenance Mode</Label>
                            <p className="text-xs text-[#A0AAB2]">Disable all games temporarily</p>
                        </div>
                        <Switch
                            checked={settings.general?.maintenance_mode || false}
                            onCheckedChange={(v) => handleSettingsUpdate('general', 'maintenance_mode', v)}
                            data-testid="maintenance-toggle"
                        />
                    </div>
                    <div className="flex items-center justify-between">
                        <div>
                            <Label className="text-white">Sound Enabled</Label>
                            <p className="text-xs text-[#A0AAB2]">Default sound state for users</p>
                        </div>
                        <Switch
                            checked={settings.general?.sound_enabled !== false}
                            onCheckedChange={(v) => handleSettingsUpdate('general', 'sound_enabled', v)}
                        />
                    </div>
                    <div className="flex items-center justify-between">
                        <div>
                            <Label className="text-white">Self-Registration</Label>
                            <p className="text-xs text-[#A0AAB2]">Allow new customers to sign up themselves via phone + password at <span className="font-mono">/register</span>. Turn this off once your customer base is onboarded.</p>
                        </div>
                        <Switch
                            checked={settings.general?.self_registration_enabled !== false}
                            onCheckedChange={(v) => handleSettingsUpdate('general', 'self_registration_enabled', v)}
                            data-testid="self-registration-toggle"
                        />
                    </div>
                    <div>
                        <Label className="text-white">New User Bonus</Label>
                        <Input
                            type="number"
                            value={settings.general?.new_user_bonus || 0}
                            onChange={(e) => handleSettingsUpdate('general', 'new_user_bonus', parseFloat(e.target.value) || 0)}
                            className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                            data-testid="welcome-bonus-input"
                        />
                    </div>
                    <div>
                        <Label className="text-white">Free Play Bonus Min Load ($)</Label>
                        <p className="text-xs text-[#A0AAB2] mt-0.5 mb-1">
                            Minimum single deposit (in last 24h) a player must make to unlock their Daily Free Play Bonus. Set 0 to disable the gate.
                        </p>
                        <Input
                            type="number"
                            step="1"
                            min="0"
                            value={settings.general?.daily_bonus_min_load ?? 20}
                            onChange={(e) => handleSettingsUpdate('general', 'daily_bonus_min_load', parseFloat(e.target.value) || 0)}
                            className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                            data-testid="daily-bonus-min-load-input"
                        />
                    </div>
                    <div>
                        <Label className="text-white">Free Play Bonus Base Amount</Label>
                        <p className="text-xs text-[#A0AAB2] mt-0.5 mb-1">Free-play credits awarded on day 1 of streak. Lands in the non-cashable promo wallet — players can play any game but cannot directly withdraw the bonus.</p>
                        <Input
                            type="number"
                            step="1"
                            min="0"
                            value={settings.general?.daily_bonus_base ?? 10}
                            onChange={(e) => handleSettingsUpdate('general', 'daily_bonus_base', parseFloat(e.target.value) || 0)}
                            className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                            data-testid="daily-bonus-base-input"
                        />
                    </div>
                    <div>
                        <Label className="text-white">🛑 Daily Bonus Hard Cap ($)</Label>
                        <p className="text-xs text-[#A0AAB2] mt-0.5 mb-1">
                            Absolute maximum a single daily bonus grant can be, after streak + VIP multipliers. Set 0 for no cap. Use this to slow down whales / long streaks.
                        </p>
                        <Input
                            type="number"
                            step="1"
                            min="0"
                            value={settings.general?.daily_bonus_max_amount ?? 25}
                            onChange={(e) => handleSettingsUpdate('general', 'daily_bonus_max_amount', parseFloat(e.target.value) || 0)}
                            className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                            data-testid="daily-bonus-cap-input"
                        />
                    </div>
                    <div>
                        <Label className="text-white">Streak Bonus per Extra Day ($)</Label>
                        <p className="text-xs text-[#A0AAB2] mt-0.5 mb-1">
                            Added per consecutive day after day 1. Example: $2 here means day 3 = base + $4. Set 0 to disable streak bonuses.
                        </p>
                        <Input
                            type="number"
                            step="0.5"
                            min="0"
                            value={settings.general?.daily_streak_bonus ?? 5}
                            onChange={(e) => handleSettingsUpdate('general', 'daily_streak_bonus', parseFloat(e.target.value) || 0)}
                            className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                            data-testid="daily-streak-bonus-input"
                        />
                    </div>
                    <div>
                        <Label className="text-white">Max Streak Days</Label>
                        <p className="text-xs text-[#A0AAB2] mt-0.5 mb-1">
                            Streak bonus stops growing after this many days. Lower this to stop runaway streaks. Default 7.
                        </p>
                        <Input
                            type="number"
                            step="1"
                            min="1"
                            value={settings.general?.max_daily_streak ?? 7}
                            onChange={(e) => handleSettingsUpdate('general', 'max_daily_streak', parseInt(e.target.value, 10) || 1)}
                            className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                            data-testid="daily-max-streak-input"
                        />
                    </div>
                    <div>
                        <Label className="text-white">Rollover Multiplier (×)</Label>
                        <p className="text-xs text-[#A0AAB2] mt-0.5 mb-1">
                            How many times the bonus must be wagered before any winnings convert to cashable. 1 = wager bonus once. 3 = wager 3×. Set 0 for no rollover (winnings immediately cashable). <span className="text-[#39FF14]">3× is the recommended throttle.</span>
                        </p>
                        <Input
                            type="number"
                            step="0.5"
                            min="0"
                            value={settings.general?.daily_bonus_rollover_multiplier ?? 1}
                            onChange={(e) => handleSettingsUpdate('general', 'daily_bonus_rollover_multiplier', parseFloat(e.target.value) || 0)}
                            className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                            data-testid="daily-rollover-input"
                        />
                    </div>
                    <div>
                        <Label className="text-white">🚦 Weekly Free-Play Cap per Player ($)</Label>
                        <p className="text-xs text-[#A0AAB2] mt-0.5 mb-1">
                            Silent cap. Once a player has received this much daily-bonus free play in the last 7 days, further qualifications are skipped or trimmed — no error shown to the player. Set 0 to disable the cap. <span className="text-[#39FF14]">$35/week recommended.</span>
                        </p>
                        <Input
                            type="number"
                            step="5"
                            min="0"
                            value={settings.general?.daily_bonus_weekly_cap ?? 0}
                            onChange={(e) => handleSettingsUpdate('general', 'daily_bonus_weekly_cap', parseFloat(e.target.value) || 0)}
                            className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                            data-testid="daily-bonus-weekly-cap-input"
                        />
                    </div>
                    <FreePlaySpendBanner />
                    <div>
                        <Label className="text-white">🎂 Birthday Bonus ($)</Label>
                        <p className="text-xs text-[#A0AAB2] mt-0.5 mb-1">
                            Credits auto-awarded once per year when a player logs in on their birthday (Central Time). Set 0 to disable.
                        </p>
                        <Input
                            type="number"
                            step="1"
                            min="0"
                            value={settings.general?.birthday_bonus_amount ?? 10}
                            onChange={(e) => handleSettingsUpdate('general', 'birthday_bonus_amount', parseFloat(e.target.value) || 0)}
                            className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                            data-testid="birthday-bonus-input"
                        />
                    </div>
                </div>
            </div>

            <div className="card-dark p-6">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-[#39FF14]" />
                    Sportsbook Settings
                </h3>
                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label className="text-white">Min Bet</Label>
                            <Input
                                type="number"
                                value={settings.sportsbook?.min_bet || 1}
                                onChange={(e) => handleSettingsUpdate('sportsbook', 'min_bet', parseFloat(e.target.value) || 1)}
                                className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                            />
                        </div>
                        <div>
                            <Label className="text-white">Max Bet</Label>
                            <Input
                                type="number"
                                value={settings.sportsbook?.max_bet || 10000}
                                onChange={(e) => handleSettingsUpdate('sportsbook', 'max_bet', parseFloat(e.target.value) || 10000)}
                                className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                            />
                        </div>
                    </div>
                    <div>
                        <Label className="text-white">Max Payout Per Bet</Label>
                        <Input
                            type="number"
                            value={settings.sportsbook?.max_payout || 100000}
                            onChange={(e) => handleSettingsUpdate('sportsbook', 'max_payout', parseFloat(e.target.value) || 100000)}
                            className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                        />
                    </div>
                </div>
            </div>
        </div>
        <div className="mt-6">
            <SafePanel label="broadcast-panel"><BroadcastMessagePanel /></SafePanel>
        </div>
        <div className="mt-6">
            <SafePanel label="first-load-match-campaign"><FirstLoadMatchCampaignCard /></SafePanel>
        </div>
        <div className="mt-6">
            <SafePanel label="apple-pay-domain"><ApplePayDomainCard /></SafePanel>
        </div>
        <div className="mt-6">
            <SafePanel label="admin-activity"><AdminActivityLog onNavigate={onNavigate} /></SafePanel>
        </div>
        <div className="mt-6">
            <SafePanel label="db-export"><DbExportPanel /></SafePanel>
        </div>
        <div className="mt-6">
            <SafePanel label="danger-zone"><DangerZone /></SafePanel>
        </div>
    </>
    );
};
